import { ApiClient } from '../../../utils/apiClient';
import { ApiResult, handleApiResult } from '../../../utils/apiErrorHandler';
import {
  AvailabilityException,
  AvailabilityExceptionInput,
  AvailabilityRule,
  AvailabilityRuleInput,
  Booking,
  BookingCreateInput,
  BookingListQuery,
  BookingMoveInput,
  BookingPage,
  Resource,
  ResourceInput,
  SchedulingApiError,
  SchedulingProfile,
  SchedulingProfileInput,
  Service,
  ServiceInput,
  ServiceVariant,
  ServiceVariantInput,
} from './types';

type Query = Record<string, string | number | boolean | undefined>;

const toQueryString = (query?: Query): string => {
  const params = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const unwrap = <T>(result: ApiResult<T>, fallback: string): T | undefined => {
  if (result.success) return result.data;
  const errors = result.errors;
  throw new SchedulingApiError(
    errors?.generalError || errors?.nonFieldErrors?.[0] || fallback,
    errors?.fieldErrors,
    errors?.nonFieldErrors,
    errors?.code
  );
};

// The scheduling endpoints are unpaginated and share the same CRUD shape.
const crud = <T, I>(path: string, label: string) => ({
  list: async (query?: Query): Promise<T[]> => {
    const response = await ApiClient.get(`/calendars/${path}/${toQueryString(query)}`);
    const data = unwrap<T[] | { results: T[] }>(
      await handleApiResult(response, `Failed to load ${label}`),
      `Failed to load ${label}`
    );
    if (!data) return [];
    return Array.isArray(data) ? data : data.results;
  },
  get: async (id: string): Promise<T> => {
    const response = await ApiClient.get(`/calendars/${path}/${id}/`);
    return unwrap<T>(
      await handleApiResult(response, `Failed to load ${label}`),
      `Failed to load ${label}`
    ) as T;
  },
  create: async (data: I): Promise<T> => {
    const response = await ApiClient.post(`/calendars/${path}/`, data);
    return unwrap<T>(
      await handleApiResult(response, `Failed to create ${label}`),
      `Failed to create ${label}`
    ) as T;
  },
  update: async (id: string, data: I): Promise<T> => {
    const response = await ApiClient.patch(`/calendars/${path}/${id}/`, data);
    return unwrap<T>(
      await handleApiResult(response, `Failed to update ${label}`),
      `Failed to update ${label}`
    ) as T;
  },
  remove: async (id: string): Promise<void> => {
    const response = await ApiClient.delete(`/calendars/${path}/${id}/`);
    unwrap<void>(
      await handleApiResult(response, `Failed to delete ${label}`),
      `Failed to delete ${label}`
    );
  },
});

export const schedulingProfileService = crud<SchedulingProfile, SchedulingProfileInput>(
  'scheduling-profiles',
  'calendar'
);
export const resourceService = crud<Resource, ResourceInput>('resources', 'resource');
export const serviceService = crud<Service, ServiceInput>('services', 'service');
export const serviceVariantService = crud<ServiceVariant, ServiceVariantInput>(
  'service-variants',
  'variant'
);
export const availabilityRuleService = crud<AvailabilityRule, AvailabilityRuleInput>(
  'availability-rules',
  'opening hours'
);
export const availabilityExceptionService = crud<AvailabilityException, AvailabilityExceptionInput>(
  'availability-exceptions',
  'exception'
);

const bookingCrud = crud<Booking, never>('bookings', 'booking');

const postBooking = async (path: string, data: object, label: string): Promise<Booking> => {
  const response = await ApiClient.post(path, data);
  try {
    return unwrap<Booking>(await handleApiResult(response, label), label) as Booking;
  } catch (error) {
    // The calendar tells "occupied" (409) apart from other failures.
    if (error instanceof SchedulingApiError) error.status = response.status;
    throw error;
  }
};

export const bookingService = {
  // Bookings of a time window come back unpaginated.
  list: (profileId: string, from: string, to: string) =>
    bookingCrud.list({ profile: profileId, start_after: from, start_before: to }),
  // The booking list is paginated, unlike the calendar window above.
  listPage: async (query: BookingListQuery): Promise<BookingPage> => {
    const response = await ApiClient.get(
      `/calendars/bookings/${toQueryString({
        profile: query.profile,
        page: query.page,
        page_size: query.pageSize,
        ordering: query.ordering,
        resource: query.resource,
        status: query.status,
        source: query.source,
        sync_status: query.syncStatus,
      })}`
    );
    const data = unwrap<BookingPage>(
      await handleApiResult(response, 'Failed to load bookings'),
      'Failed to load bookings'
    );
    return data ?? { count: 0, results: [] };
  },
  create: (data: BookingCreateInput) =>
    postBooking('/calendars/bookings/', data, 'Failed to create booking'),
  move: (id: string, data: BookingMoveInput) =>
    postBooking(`/calendars/bookings/${id}/move/`, data, 'Failed to move booking'),
  cancel: (id: string) =>
    postBooking(`/calendars/bookings/${id}/cancel/`, {}, 'Failed to cancel booking'),
  markNoShow: (id: string) =>
    postBooking(`/calendars/bookings/${id}/no-show/`, {}, 'Failed to mark booking as no-show'),
};
